import React from 'react';

/**
 * Shared visual for a single tweet. Used by both the builder feed and the
 * recipient view. Pure presentation — selection / wrapping concerns belong
 * to the consumer.
 *
 * X Display Requirements that this component satisfies:
 *   - Author name + @handle visible
 *   - Original tweet text unmodified
 *   - Timestamp visible
 *   - Link back to the original tweet on x.com
 */
const TweetCard = ({ tweet, selected = false, pills = [], showMetrics = true }) => {
  const tweetUrl = `https://x.com/${tweet.authorHandle}/status/${tweet.xTweetId}`;
  const timeShort = formatTimeShort(tweet.tweetCreatedAt);
  const timeFull = new Date(tweet.tweetCreatedAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  const initials = (tweet.authorName || '?')
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const root = {
    ...styles.card,
    ...(selected ? styles.cardSelected : {}),
  };

  return (
    <div style={root}>
      <div style={styles.topRow}>
        <div style={styles.authorBlock}>
          {tweet.authorAvatarUrl ? (
            <img
              src={tweet.authorAvatarUrl}
              alt=""
              style={styles.avatar}
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div
              style={{
                ...styles.avatar,
                ...styles.avatarFallback,
                ...(selected ? styles.avatarFallbackSelected : {}),
              }}
            >
              {initials}
            </div>
          )}
          <div style={styles.authorText}>
            <div style={styles.authorName}>{tweet.authorName}</div>
            <a
              href={`https://x.com/${tweet.authorHandle}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                ...styles.authorHandle,
                ...(selected ? styles.mutedSelected : {}),
              }}
            >
              @{tweet.authorHandle}
            </a>
          </div>
        </div>
        <a
          href={tweetUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={timeFull}
          onClick={(e) => e.stopPropagation()}
          style={{
            ...styles.timestamp,
            ...(selected ? styles.mutedSelected : {}),
          }}
        >
          {timeShort}
        </a>
      </div>

      <div style={styles.text}>{decodeEntities(tweet.text)}</div>

      <div style={styles.bottomRow}>
        <div style={styles.pills}>
          {pills.map((p, i) => (
            <span
              key={i}
              style={{
                ...styles.pill,
                ...(p.variant === 'side' ? styles.pillSide : {}),
                ...(selected ? styles.pillSelected : {}),
              }}
            >
              {p.label}
            </span>
          ))}
        </div>
        <div style={styles.metricsRow}>
          {showMetrics && tweet.metrics && (
            <span
              style={{
                ...styles.metricsText,
                ...(selected ? styles.mutedSelected : {}),
              }}
            >
              {formatMetrics(tweet.metrics)}
            </span>
          )}
          <a
            href={tweetUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              ...styles.viewLink,
              ...(selected ? styles.viewLinkSelected : {}),
            }}
          >
            View on X →
          </a>
        </div>
      </div>
    </div>
  );
};

// X API returns tweet text with HTML entities (&amp;, &lt;, &gt;, &quot;, &#39;).
// Decode them so the user-facing text reads naturally.
const decodeEntities = (str) => {
  if (!str) return '';
  if (typeof document === 'undefined') return str;
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
};

const formatTimeShort = (d) => {
  const dt = new Date(d);
  const diffMs = Date.now() - dt.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatMetrics = (m) => {
  if (!m) return '';
  const parts = [];
  if (m.like_count > 0) parts.push(`${formatCompact(m.like_count)} ${m.like_count === 1 ? 'like' : 'likes'}`);
  if (m.reply_count > 0) parts.push(`${formatCompact(m.reply_count)} ${m.reply_count === 1 ? 'reply' : 'replies'}`);
  if (m.retweet_count > 0) parts.push(`${formatCompact(m.retweet_count)} ${m.retweet_count === 1 ? 'repost' : 'reposts'}`);
  if (parts.length === 0 && m.impression_count > 0) {
    parts.push(`${formatCompact(m.impression_count)} views`);
  }
  return parts.join(' · ');
};

const formatCompact = (n) => {
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
};

const styles = {
  card: {
    background: '#FFFFFF',
    borderRadius: '18px',
    padding: '20px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    color: '#1D1D1F',
    minWidth: 0,
    width: '100%',
    border: '1px solid #EBEBED',
  },
  cardSelected: {
    background: '#000000',
    color: '#FFFFFF',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  },
  authorBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
    flex: 1,
  },
  avatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
    background: '#EBEBED',
  },
  avatarFallback: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    fontWeight: 600,
    color: '#6E6E73',
    background: '#EBEBED',
  },
  avatarFallbackSelected: {
    background: 'rgba(255, 255, 255, 0.12)',
    color: '#FFFFFF',
  },
  authorText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  authorName: {
    fontSize: '15px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  authorHandle: {
    fontSize: '13px',
    color: '#86868B',
    textDecoration: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  timestamp: {
    fontSize: '13px',
    color: '#86868B',
    textDecoration: 'none',
    flexShrink: 0,
    paddingTop: '2px',
  },
  mutedSelected: {
    color: 'rgba(255, 255, 255, 0.55)',
  },
  text: {
    fontSize: '17px',
    lineHeight: 1.45,
    letterSpacing: '-0.01em',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bottomRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  pills: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  pill: {
    fontSize: '12px',
    fontWeight: 500,
    padding: '4px 10px',
    borderRadius: '980px',
    background: 'rgba(0, 0, 0, 0.05)',
    color: 'inherit',
    letterSpacing: '0',
  },
  pillSide: {
    background: 'rgba(0, 0, 0, 0.08)',
    fontWeight: 600,
  },
  pillSelected: {
    background: 'rgba(255, 255, 255, 0.14)',
  },
  metricsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginLeft: 'auto',
  },
  metricsText: {
    fontSize: '13px',
    color: '#86868B',
  },
  viewLink: {
    fontSize: '13px',
    color: '#86868B',
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'opacity 0.2s',
  },
  viewLinkSelected: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
};

export default TweetCard;
